import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  MapPin,
  MapPinned,
  Phone,
  Plus,
  Settings2,
  Tag,
  Trash2,
  Upload,
} from "lucide-react";
import L from "leaflet";
import {
  type FormEvent,
  type ReactNode,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from "react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import { toast } from "sonner";

import { readImage } from "@/components/avatar";
import { DetailPanel, FormPanel } from "@/components/resource-page";
import { Button, IconButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog, Modal } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Page, PageHeader, Toolbar } from "@/components/ui/page";
import { SearchInput } from "@/components/ui/search-input";
import { Segmented } from "@/components/ui/tabs";
import { RefreshButton } from "@/components/ui/refresh-button";
import { LoadingNote } from "@/components/ui/loading";
import { Skeleton } from "@/components/ui/skeleton";
import { Field, Input } from "@/components/ui/field";
import { FkSelect, fetchCompact } from "@/components/ui/fk-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import { can as allowed } from "@/utils/permissions";
import { listResource } from "@/utils/geo";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { profileQuery } from "@/services/queries";
import { useTranslation } from "@/store/locale";
import { useLoadMore } from "@/hooks/use-load-more";
import { useDelayed } from "@/hooks/use-delayed";
import { useRecordParam } from "@/hooks/use-record-param";
import { cn } from "@/utils/utils";

/** Which of the viewer's two clubs the route sets out from. */
type Origin = "home" | "posted";

interface Listing {
  id: number;
  updated_at?: string;
  name: string;
  category_id: number;
  category_name: string | null;
  city_id: number;
  city_name: string | null;
  full_address: string;
  phone: string | null;
  latitude: number;
  longitude: number;
  photos: string[];
  is_favorite: boolean;
}

const LIST_PAGE = 12;

function YellowPagePage() {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // `endsWith("__yellow_page")` matched `view__yellow_page` too, so a reader was
  // shown buttons the API would refuse — and, once the detail panel gained one,
  // a Delete. Each action asks for its own codename.
  const can = (action: string) => allowed(profile, `${action}__yellow_page`);
  const canAdd = can("add");
  const canChange = can("change");
  const canDelete = can("delete");

  const [search, setSearch] = useState("");
  const deferred = useDeferredValue(search);
  const [cityId, setCityId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [favorites, setFavorites] = useState(false);
  const [deleting, setDeleting] = useState<Listing | null>(null);
  const [refreshingOne, setRefreshingOne] = useState(false);

  // Where the viewer is, for directions. The club they work from today leads —
  // a temporary posting while it applies, their home club otherwise — but on a
  // posting both are real places to set out from, so both are offered and the
  // active one is merely the default.
  const homeId = profile?.club?.id;
  const postedId = profile?.temporary_club?.id;
  const activeOrigin: Origin =
    profile?.has_active_temporary_club && postedId ? "posted" : "home";
  const [pickedOrigin, setPickedOrigin] = useState<Origin | null>(null);
  // Null until they choose, so the default keeps following the posting rather
  // than freezing whatever was active on the first render.
  const originKey = pickedOrigin ?? activeOrigin;

  const club = (id: number | undefined) =>
    queryOptions({
      queryKey: ["/clubs", id],
      queryFn: async ({ signal }) =>
        (
          await api.get<{
            club_name: string;
            latitude: number | null;
            longitude: number | null;
          }>(`/clubs/${id}`, { signal })
        ).data,
      enabled: Boolean(id),
      staleTime: 300_000,
    });
  const homeClub = useQuery(club(homeId));
  const postedClub = useQuery(club(postedId));
  const fromClub = (originKey === "posted" ? postedClub : homeClub).data ?? null;

  // Only worth a switch when there are two places to switch between.
  const originSwitch =
    postedId && homeId ? (
      <Segmented
        label={t("yp.setOutFrom")}
        value={originKey}
        onChange={setPickedOrigin}
        items={[
          {
            value: "home" as const,
            label: homeClub.data?.club_name.toUpperCase() ?? t("yp.homeClub"),
          },
          {
            value: "posted" as const,
            label: postedClub.data?.club_name.toUpperCase() ?? t("yp.temporaryClub"),
          },
        ]}
      />
    ) : null;

  // A dozen at a time; the next dozen when the list is scrolled to its end.
  const filters = { search: deferred, city_id: cityId, category_id: categoryId, favorites };
  const listings = useInfiniteQuery({
    queryKey: ["/yellow-pages", "infinite", filters],
    queryFn: ({ pageParam, signal }) =>
      listResource<Listing>(
        "/yellow-pages",
        {
          search: deferred,
          city_id: cityId,
          category_id: categoryId,
          favorites: favorites || undefined,
          page: pageParam,
          size: LIST_PAGE,
        },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/yellow-pages"] });
  // A refresh keeps the rows, so there is no skeleton to put up — the listings
  // dim and a note says what the wait is for, the way every other list here
  // behaves. Immediate for the controls, delayed for the note: a loader that
  // flashes is a glitch, but a filter that stays live for 300ms is a request
  // queued behind the one already out.
  const busy = listings.isFetching;
  const rereading = useDelayed(busy);
  const rows = listings.data?.pages.flatMap((page) => page.items) ?? [];
  const total = listings.data?.pages[0]?.total ?? 0;

  // Which listing is open, and which is being edited, both live in the address
  // bar — so a reload comes back to the same place, as on every other screen.
  const {
    editing,
    setEditing,
    loading: openingEdit,
  } = useRecordParam<Listing>("edit", "yellow-page", "/yellow-pages");
  const {
    editing: viewing,
    setEditing: setViewing,
    loading: openingView,
  } = useRecordParam<Listing>("view", "yellow-page", "/yellow-pages");

  // Who added or last changed a listing: names, not the ids the record holds.
  const users = useQuery({
    queryKey: ["compact", "/users/compact", "all"],
    queryFn: ({ signal }) => fetchCompact("/users/compact", { limit: 200 }, signal),
    staleTime: 300_000,
  });
  const lookup = (_: string, value: string | number | null) =>
    value === null
      ? "—"
      : (users.data?.find((user) => String(user.id) === String(value))?.name ?? String(value));

  const list = useRef<HTMLUListElement>(null);
  useLoadMore(
    list,
    listings.hasNextPage,
    listings.isFetchingNextPage,
    () => void listings.fetchNextPage(),
  );

  const tag = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? api.put(`/yellow-pages/${id}/favorite`) : api.delete(`/yellow-pages/${id}/favorite`),
    onSuccess: (_, { on }) => {
      toast.success(on ? t("yp.tagged") : t("yp.tagRemoved"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("yp.tagFailed"))),
  });
  const remove = useMutation({
    mutationFn: (listing: Listing) =>
      api.delete(`/yellow-pages/${listing.id}`, ifMatch(listing)),
    onSuccess: () => {
      toast.success(t("yp.listingDeleted"));
      setViewing(null);
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("yp.deleteFailed"))),
  });

  // Adding, editing and viewing take the page over, exactly as they do on
  // Provinces — a form that replaces what it is a form for, and a record read
  // where it was opened rather than in a sheet over the list.
  if (editing !== null) {
    return (
      <Page>
        <ListingForm
          listing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      </Page>
    );
  }

  // The listing is read when it is opened, so there is a wait between the click
  // and the panel. The list would otherwise sit there looking as if the click
  // had missed.
  if (openingView || openingEdit) {
    return (
      <Page>
        <PageHeader
          back={
            <button
              type="button"
              onClick={() => {
                setViewing(null);
                setEditing(null);
              }}
              className="text-fg-muted hover:text-fg text-callout"
            >
              ← {t("yp.listing")}
            </button>
          }
          eyebrow={t("yp.listing")}
          title={<Skeleton className="h-7 w-56" />}
        />
        <LoadingNote subject={t("yp.listing").toLowerCase()} className="mb-3" />
        <section className="bg-surface-raised rounded-card border-line grid gap-4 border px-6 py-5">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className={index % 3 === 0 ? "h-4 w-1/2" : "h-4 w-2/3"} />
            </div>
          ))}
        </section>
      </Page>
    );
  }

  if (viewing !== null && viewing !== "new") {
    return (
      <Page>
        <DetailPanel
          singular={t("yp.listing")}
          row={viewing}
          labels={{
            category_name: t("ypc.category"),
            city_name: t("geo.city"),
            full_address: t("yp.fullAddress"),
            phone: t("yp.phone"),
            name: t("geo.name"),
          }}
          lookup={lookup}
          onClose={() => setViewing(null)}
          busy={refreshingOne}
          onRefresh={() => {
            setRefreshingOne(true);
            void invalidate().finally(() => setRefreshingOne(false));
          }}
          onEdit={canChange ? () => setEditing(viewing) : undefined}
          onDelete={canDelete ? () => setDeleting(viewing) : undefined}
          hide={["latitude", "longitude", "is_favorite"]}
          extra={<ListingMap listing={viewing} from={fromClub} originSwitch={originSwitch} />}
        />
        <ConfirmDialog
          open={deleting !== null}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (deleting) remove.mutate(deleting);
            setDeleting(null);
          }}
          title={t("yp.deleteListing")}
          message={t("yp.confirmDelete").replace(
            "{name}",
            deleting?.name ?? t("yp.thisListing"),
          )}
        />
      </Page>
    );
  }

  const filtered = Boolean(search || cityId || categoryId || favorites);

  return (
    <Page fill>
      <PageHeader
        title={t("yp.title")}
        description={t("yp.description")}
        actions={
          canAdd || canChange ? (
            <fieldset disabled={busy} className="contents">
              {/* An anchor, so the fieldset around this cannot reach it — the
                  three-part disable instead, the same one the sign-in links
                  use. It leads to a screen that reads the categories this list
                  is filtered by, so it waits with the rest. */}
              <Button asChild variant="secondary">
                <Link
                  to="/yellow-page-categories"
                  aria-disabled={busy || undefined}
                  tabIndex={busy ? -1 : undefined}
                  className={cn(busy && "pointer-events-none opacity-50")}
                >
                  <Settings2 aria-hidden /> {t("yp.categories")}
                </Link>
              </Button>
              {canAdd ? (
                <Button onClick={() => setEditing("new")}>
                  <Plus aria-hidden /> {t("yp.addListing")}
                </Button>
              ) : null}
            </fieldset>
          ) : undefined
        }
      />

      <Toolbar className="grid gap-2.5 md:grid-cols-[minmax(0,1fr)_13rem_13rem_auto]">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch("")}
          aria-busy={busy}
          placeholder={t("yp.searchListings")}
          aria-label={t("yp.searchListings")}
        />
        {/* `contents`, so the fieldset holds these without becoming a grid cell
            of its own. The search box is deliberately outside it: with a 400ms
            debounce, typing is what starts the read, and disabling the box
            mid-word is how the members search used to strand people. */}
        <fieldset disabled={busy} className="contents">
          <FkSelect
            endpoint="/cities/compact"
            value={cityId}
            onChange={setCityId}
            placeholder={t("yp.anyCity")}
          />
          <FkSelect
            endpoint="/yellow-page-categories/compact"
            value={categoryId}
            onChange={setCategoryId}
            placeholder={t("yp.anyCategory")}
          />
          <span className="flex items-center gap-2.5">
            <Button
              variant="secondary"
              aria-pressed={favorites}
              className={cn(favorites && "border-accent text-accent bg-accent-soft")}
              onClick={() => setFavorites((f) => !f)}
            >
              <Tag className={cn(favorites && "fill-current")} aria-hidden /> {t("yp.myTags")}
            </Button>
            {/* Every other list has one; this screen is not a `ResourcePage`, so
              it did not inherit it. Keyed on the endpoint rather than on the
              filtered query, so it re-reads whatever the filters are showing. */}
            <RefreshButton queryKeys={[["/yellow-pages"]]} />
          </span>
        </fieldset>
      </Toolbar>

      {rereading ? <LoadingNote className="mt-3" subject={t("yp.listings")} /> : null}

      <Card className="mt-5 flex min-h-0 flex-1 flex-col overflow-hidden">
        <ul
          ref={list}
          aria-busy={busy || undefined}
          className={cn(
            "grid content-start gap-3 overflow-auto p-4",
            "transition-opacity duration-[var(--duration-base)]",
            busy && rows.length > 0 && "opacity-55",
          )}
        >
          {rows.map((row) => (
            <li
              key={row.id}
              onClick={() => setViewing(row)}
              className="bg-surface-raised rounded-card flex cursor-pointer gap-4 p-4 shadow-[var(--shadow-lift)] transition-[box-shadow,transform] duration-[var(--duration-fast)] hover:shadow-[var(--shadow-raised)]"
            >
              <div className="bg-surface-inset text-fg-subtle grid size-24 shrink-0 place-items-center overflow-hidden rounded-xl md:size-28">
                {row.photos[0] ? (
                  <img src={row.photos[0]} alt="" className="size-full object-cover" />
                ) : (
                  <ImageIcon className="size-7" aria-hidden />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-eyebrow text-accent">{row.category_name}</p>
                <h2 className="text-title-3 text-heading mt-1 truncate">{row.name}</h2>
                {row.phone ? (
                  <p className="text-fg text-callout mt-2 flex items-center gap-2">
                    <Phone className="text-fg-muted size-4 shrink-0" aria-hidden />
                    {row.phone}
                  </p>
                ) : null}
                <p className="text-fg text-callout mt-1.5 flex items-start gap-2">
                  <MapPin className="text-fg-muted mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    {row.full_address}
                    {row.city_name ? `, ${row.city_name}` : ""}
                  </span>
                </p>
              </div>
              <button
                type="button"
                aria-pressed={row.is_favorite}
                title={row.is_favorite ? t("yp.removeTag") : t("yp.tagFavourite")}
                onClick={(e) => {
                  e.stopPropagation();
                  tag.mutate({ id: row.id, on: !row.is_favorite });
                }}
                className={cn(
                  "grid size-10 shrink-0 place-items-center self-start rounded-full transition-colors",
                  row.is_favorite
                    ? "bg-accent-soft text-accent"
                    : "text-fg-subtle hover:bg-surface-inset hover:text-accent",
                )}
              >
                <Tag className={cn("size-5", row.is_favorite && "fill-current")} aria-hidden />
                <span className="sr-only">
                  {row.is_favorite ? t("yp.removeTag") : t("yp.tagFavourite")}
                </span>
              </button>
            </li>
          ))}
          {listings.isPending || listings.isFetchingNextPage
            ? Array.from({ length: 3 }, (_, index) => (
                <li
                  key={`skeleton-${index}`}
                  className="bg-surface-raised rounded-card flex gap-4 p-4 shadow-[var(--shadow-lift)]"
                >
                  <Skeleton className="size-24 rounded-xl md:size-28" />
                  <div className="grid flex-1 content-start gap-2 pt-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="mt-2 h-3 w-1/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </li>
              ))
            : null}
          {!listings.isPending && rows.length === 0 ? (
            <li>
              <EmptyState
                kind={filtered ? "search" : "empty"}
                title={t("yp.noListings")}
                description={filtered ? t("yp.tryDifferent") : t("yp.listingsAppear")}
              />
            </li>
          ) : null}
        </ul>

        {/* How much of the list you have seen — the question a list with no
            pages actually raises. */}
        <p className="hairline-t text-fg-muted text-subhead shrink-0 px-5 py-3 text-right tabular-nums">
          {t("yp.countOf")
            .replace("{shown}", rows.length.toLocaleString())
            .replace("{total}", total.toLocaleString())}
        </p>
      </Card>
    </Page>
  );
}

/** Straight-line distance in km between two points — the fallback when no route comes back. */
function kmBetween(a: [number, number], b: [number, number]): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const h =
    Math.sin(rad(lat2 - lat1) / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lon2 - lon1) / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** One instruction, as the routing service gives it. */
interface Step {
  distance: number;
  name: string;
  maneuver: { type: string; modifier?: string };
}
interface Route {
  /** Metres and seconds along the road, not across the map. */
  distance: number;
  duration: number;
  /** GeoJSON: longitude first, which is the opposite of what Leaflet wants. */
  geometry: { coordinates: [number, number][] };
  legs: { steps: Step[] }[];
}

const TURNS: Record<string, TranslationKey> = {
  left: "yp.turnLeft",
  right: "yp.turnRight",
  "slight left": "yp.bearLeft",
  "slight right": "yp.bearRight",
  "sharp left": "yp.sharpLeft",
  "sharp right": "yp.sharpRight",
  straight: "yp.straight",
  uturn: "yp.uturn",
};

/** "Turn right onto Aurora Blvd" — the step in the words a driver would use. */
function instruction(t: Translate, step: Step, last: boolean): string {
  const { type, modifier } = step.maneuver;
  const road = step.name ? ` ${t("yp.onto").replace("{road}", step.name)}` : "";
  if (last) return t("yp.arrive");
  if (type === "depart")
    return step.name ? t("yp.headOutOn").replace("{road}", step.name) : t("yp.headOut");
  if (type === "roundabout" || type === "rotary") return `${t("yp.roundabout")}${road}`;
  if (type === "merge") return `${t("yp.merge")}${road}`;
  if (type === "fork")
    return `${t("yp.keep").replace("{direction}", modifier ?? t("yp.ahead"))}${road}`;
  if (type === "new name") return `${t("yp.continue")}${road}`;
  const turn = modifier ? TURNS[modifier] : undefined;
  return `${turn ? t(turn) : t("yp.continue")}${road}`;
}

const metres = (value: number) =>
  value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
const minutes = (seconds: number) => {
  const total = Math.round(seconds / 60);
  return total >= 60 ? `${Math.floor(total / 60)} h ${total % 60} min` : `${total} min`;
};

/** A dot with a label above it, in the app's own tokens rather than Leaflet's blue pin. */
function marker(label: string, tone: "origin" | "target"): L.DivIcon {
  const safe = label.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  const dot = tone === "origin" ? "bg-fg" : "bg-accent";
  return L.divIcon({
    className: "",
    iconAnchor: [0, 0],
    html: `<div class="pointer-events-none -translate-x-1/2 -translate-y-full flex flex-col items-center gap-0.5 whitespace-nowrap">
      <span class="text-fg text-footnote font-semibold drop-shadow-[0_0_2px_var(--surface)]">${safe}</span>
      <span class="${dot} size-2.5 rounded-full ring-2 ring-white translate-y-1"></span>
    </div>`,
  });
}

/** Frames both ends, and the road between them, whenever the route changes. */
function FitRoute({ points }: { points: [number, number][] }) {
  const map = useMap();
  const key = points.length;
  useEffect(() => {
    if (points.length === 0) return;
    map.fitBounds(L.latLngBounds(points), { padding: [32, 32] });
    // The bounds object is rebuilt every render; the point count is what changes.
  }, [map, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/**
 * The way from the club the viewer works from today to the listing — drawn and
 * written out here rather than handed to someone else's site.
 *
 * It used to be an OpenStreetMap embed with a marker and two "directions" links
 * that left the app, and the only distance it could give was across the map
 * rather than along the road. Leaflet was already a dependency for the member
 * map, so the route is a polyline over the same tiles, and the service's own
 * steps become the list under it.
 *
 * It folds away, and it is only ever built for the listing that is open — so
 * "no listing selected" is already "no map", with nothing to close.
 *
 * ponytail: OSRM's public demo server, called from the browser exactly as the
 * tiles are. Self-host it, or put a keyed provider behind `/maps/route`, if
 * this ever carries real traffic.
 */
function ListingMap({
  listing,
  from,
  originSwitch,
}: {
  listing: Listing;
  from: { club_name: string; latitude: number | null; longitude: number | null } | null;
  /** Which club to set out from, when the viewer is posted to a second one. */
  originSwitch: ReactNode;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(true);
  const [showSteps, setShowSteps] = useState(false);
  const origin: [number, number] | null =
    from && from.latitude !== null && from.longitude !== null
      ? [from.latitude, from.longitude]
      : null;
  const target: [number, number] = [listing.latitude, listing.longitude];

  const route = useQuery({
    queryKey: ["route", origin, target],
    queryFn: async ({ signal }): Promise<Route | null> => {
      if (!origin) return null;
      // Plain `fetch`, not the app's client: this is somebody else's host, and
      // sending our session header to it would be wrong. It takes the signal
      // all the same — a route nobody is waiting for should stop being drawn.
      const path = `${origin[1]},${origin[0]};${target[1]},${target[0]}`;
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${path}` +
          "?overview=full&geometries=geojson&steps=true",
        { signal },
      );
      if (!response.ok) throw new Error(`Routing failed: ${response.status}`);
      const body = (await response.json()) as { routes?: Route[] };
      return body.routes?.[0] ?? null;
    },
    enabled: origin !== null,
    staleTime: 3_600_000,
    retry: 1,
  });

  // Leaflet takes latitude first; GeoJSON gives longitude first.
  const line: [number, number][] =
    route.data?.geometry.coordinates.map(([lng, lat]) => [lat, lng]) ?? [];
  const points = line.length ? line : origin ? [origin, target] : [target];
  const steps = route.data?.legs.flatMap((leg) => leg.steps) ?? [];

  // The road distance when there is a route, the straight line when there is not.
  const summary = route.data
    ? t("yp.byCar")
        .replace("{distance}", metres(route.data.distance))
        .replace("{time}", minutes(route.data.duration))
    : origin
      ? t("yp.straightLine").replace("{km}", kmBetween(origin, target).toFixed(1))
      : `${listing.latitude.toFixed(5)}, ${listing.longitude.toFixed(5)}`;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-fg-subtle text-eyebrow tracking-[0.04em] uppercase">
          {from
            ? t("yp.directionsFrom").replace("{club}", from.club_name.toUpperCase())
            : t("yp.location")}
        </h3>
        {originSwitch}
        <Button variant="ghost" size="sm" onClick={() => setOpen((was) => !was)}>
          {open ? (
            <>
              <ChevronUp aria-hidden /> {t("yp.hideMap")}
            </>
          ) : (
            <>
              <ChevronDown aria-hidden /> {t("yp.showMap")}
            </>
          )}
        </Button>
      </div>

      <p className="text-fg text-callout mt-1 flex items-center gap-2">
        <MapPinned className="text-accent size-4 shrink-0" aria-hidden />
        {route.isPending && origin ? t("yp.findingWay") : summary}
      </p>
      {route.isError ? (
        <p className="text-fg-muted text-subhead mt-1">{t("yp.routeFailed")}</p>
      ) : null}

      {open ? (
        <MapContainer
          center={target}
          zoom={13}
          scrollWheelZoom={false}
          className="rounded-card mt-3 h-[clamp(26rem,62dvh,46rem)] w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {line.length ? (
            // The colour rides on a class, not the `color` option: Leaflet writes
            // that to the SVG `stroke` attribute, where `var()` does not resolve.
            // A CSS declaration beats a presentation attribute, so the line
            // follows the theme.
            <Polyline
              positions={line}
              pathOptions={{ className: "stroke-accent", weight: 5 }}
            />
          ) : null}
          {origin && from ? (
            <Marker position={origin} icon={marker(from.club_name.toUpperCase(), "origin")} />
          ) : null}
          <Marker position={target} icon={marker(listing.name, "target")} />
          <FitRoute points={points} />
        </MapContainer>
      ) : null}

      {steps.length ? (
        <div className="mt-3">
          <Button variant="ghost" size="sm" onClick={() => setShowSteps((was) => !was)}>
            {showSteps ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
            {t(showSteps ? "yp.hideSteps" : "yp.showSteps").replace(
              "{n}",
              String(steps.length),
            )}
          </Button>
          {showSteps ? (
            <ol className="divide-line border-line mt-2 divide-y rounded-lg border">
              {steps.map((step, index) => (
                <li
                  key={index}
                  className="text-callout flex items-baseline justify-between gap-4 px-3 py-2"
                >
                  <span className="text-fg min-w-0">
                    <span className="text-fg-subtle mr-2 tabular-nums">{index + 1}.</span>
                    {instruction(t, step, index === steps.length - 1)}
                  </span>
                  <span className="text-fg-muted text-subhead shrink-0 tabular-nums">
                    {step.distance > 0 ? metres(step.distance) : ""}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}

      {listing.photos.length ? (
        <ul className="mt-4 flex flex-wrap gap-3">
          {listing.photos.map((src_, index) => (
            <li key={index}>
              <img src={src_} alt="" className="size-24 rounded-lg object-cover" />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

const MAX_PHOTOS = 5;

/** The panel footer's Save reaches the form by name; only one is ever open. */
const FORM_ID = "listing-form";

function ListingForm({
  listing,
  onClose,
  onSaved,
}: {
  listing: Listing | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();

  const [values, setValues] = useState({
    name: listing?.name ?? "",
    category_id: listing?.category_id.toString() ?? "",
    city_id: listing?.city_id.toString() ?? "",
    full_address: listing?.full_address ?? "",
    phone: listing?.phone ?? "",
    latitude: listing?.latitude.toString() ?? "",
    longitude: listing?.longitude.toString() ?? "",
  });
  const [photos, setPhotos] = useState<string[]>(listing?.photos ?? []);
  const [error, setError] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState<string | null>(null);
  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }));

  const addCategory = async (name: string) => {
    if (!name.trim()) return;
    try {
      const created = (
        await api.post<{ id: number; name: string }>("/yellow-page-categories", {
          name: name.trim(),
        })
      ).data;
      await queryClient.invalidateQueries({ queryKey: ["options", "yellow-page-categories"] });
      set("category_id", String(created.id));
      setNewCategory(null);
      toast.success(t("yp.categoryAdded").replace("{name}", created.name));
    } catch (err) {
      toast.error(errorMessage(err, t("yp.categoryFailed")));
    }
  };

  const addPhotos = async (files: FileList | File[] | null) => {
    if (!files) return;
    const room = MAX_PHOTOS - photos.length;
    const picked = [...files].filter((f) => f.type.startsWith("image/")).slice(0, room);
    if (picked.length === 0) return;
    try {
      const urls = await Promise.all(picked.map((file) => readImage(file, 800)));
      setPhotos((current) => [...current, ...urls].slice(0, MAX_PHOTOS));
    } catch (err) {
      toast.error(errorMessage(err, t("yp.imageFailed")));
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: values.name,
        category_id: Number(values.category_id),
        city_id: Number(values.city_id),
        full_address: values.full_address,
        phone: values.phone || null,
        latitude: Number(values.latitude),
        longitude: Number(values.longitude),
        photos,
      };
      return listing
        ? api.patch(`/yellow-pages/${listing.id}`, body, ifMatch(listing))
        : api.post("/yellow-pages", body);
    },
    onSuccess: () => {
      toast.success(listing ? t("yp.listingUpdated") : t("yp.listingAdded"));
      onSaved();
    },
    onError: (err) => setError(errorMessage(err, t("yp.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  const req = (label: string) => (
    <>
      {label}
      <span aria-hidden className="text-danger ml-0.5">
        *
      </span>
    </>
  );

  return (
    <FormPanel
      title={listing ? t("yp.editListing") : t("yp.addListing")}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="grid gap-4 md:grid-cols-2 md:items-start">
        <Field label={req(t("geo.name"))} htmlFor="yp-name">
          <Input
            id="yp-name"
            required
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label={req(t("yp.category"))} htmlFor="yp-category">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <FkSelect
                id="yp-category"
                required
                endpoint="/yellow-page-categories/compact"
                value={values.category_id}
                onChange={(next) => set("category_id", next)}
                placeholder={t("yp.selectCategory")}
              />
            </div>
            <IconButton
              label={t("yp.addCategory")}
              variant="secondary"
              onClick={() => setNewCategory("")}
            >
              <Plus aria-hidden />
            </IconButton>
          </div>
        </Field>
        <Field label={req(t("yp.location"))} htmlFor="yp-city" hint={t("yp.locationHint")}>
          <FkSelect
            id="yp-city"
            required
            endpoint="/cities/compact"
            value={values.city_id}
            onChange={(next) => set("city_id", next)}
            placeholder={t("yp.selectCity")}
          />
        </Field>
        <Field label={req(t("geo.longitude"))} htmlFor="yp-lng">
          <Input
            id="yp-lng"
            type="number"
            step="any"
            min={-180}
            max={180}
            required
            value={values.longitude}
            onChange={(e) => set("longitude", e.target.value)}
          />
        </Field>
        <Field label={req(t("yp.fullAddress"))} htmlFor="yp-address">
          <Input
            id="yp-address"
            required
            value={values.full_address}
            onChange={(e) => set("full_address", e.target.value)}
          />
        </Field>
        <Field label={req(t("geo.latitude"))} htmlFor="yp-lat">
          <Input
            id="yp-lat"
            type="number"
            step="any"
            min={-90}
            max={90}
            required
            value={values.latitude}
            onChange={(e) => set("latitude", e.target.value)}
          />
        </Field>
        <Field label={t("yp.phone")} htmlFor="yp-phone">
          <Input
            id="yp-phone"
            type="tel"
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>

        <div className="md:col-span-2">
          <span className="text-fg text-body font-semibold">{t("yp.photos")}</span>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void addPhotos(e.dataTransfer.files);
            }}
            className="border-line text-fg-muted hover:border-accent text-callout mt-1.5 flex min-h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 transition-colors"
          >
            <Upload className="size-6" aria-hidden />
            {t("yp.dropPhotos")}
            <span className="text-fg-subtle text-footnote">
              {t("yp.photosOf")
                .replace("{n}", String(photos.length))
                .replace("{max}", String(MAX_PHOTOS))}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              onChange={(e) => {
                void addPhotos(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          {photos.length ? (
            <ul className="mt-3 flex flex-wrap gap-3">
              {photos.map((src, index) => (
                <li key={index} className="relative">
                  <img src={src} alt="" className="size-20 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((p) => p.filter((_, i) => i !== index))}
                    className="bg-surface-raised text-fg-muted hover:text-danger absolute -top-2 -right-2 grid size-6 place-items-center rounded-full shadow"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    <span className="sr-only">{t("yp.removePhoto")}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout flex items-start gap-2 rounded-lg px-3 py-2.5 md:col-span-2"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </p>
        ) : null}

        <Modal
          open={newCategory !== null}
          onClose={() => setNewCategory(null)}
          size="sm"
          title={t("yp.newCategory")}
          description={t("yp.newCategoryHint")}
          footer={
            <>
              <Button variant="secondary" onClick={() => setNewCategory(null)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void addCategory(newCategory ?? "")}>{t("yp.add")}</Button>
            </>
          }
        >
          <Field label={t("geo.name")} htmlFor="yp-new-category">
            <Input
              id="yp-new-category"
              autoFocus
              required
              maxLength={80}
              value={newCategory ?? ""}
              onChange={(event) => setNewCategory(event.target.value)}
            />
          </Field>
        </Modal>
      </form>
    </FormPanel>
  );
}

export default YellowPagePage;
